"use client";

import { FC, useEffect, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { PrismicNextImage } from "@prismicio/next";
import aboutImgBg from "@/assets/aboutImgBg.svg"; 
import bannerBgShape from "@/assets/BannerBgShape.svg"; 
import Image from "next/image";
import { motion } from "framer-motion";
import { useMediaQuery } from "usehooks-ts";
import clsx from "clsx";

export type AboutSectionProps = SliceComponentProps<Content.AboutSectionSlice>;

/**
 * Component for "AboutSection" Slices.
 */
const AboutSection: FC<AboutSectionProps> = ({ slice }) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const isLargerDevice = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted]);

  if (!mounted) {
    return;
  }

  // --- 'aboutUs' Variation ---
 if (slice.variation === "aboutUs") {
    const imageField = slice.primary.right_side_image;
    const messageField = slice.primary.right_side_image_message;

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        // Updated: Dark Blue Background + Relative for positioning
        className="relative bg-[#033D61] overflow-hidden py-16 md:py-24 w-full"
      >
        {/* Hero Background Shape */}
        <Image
          src={bannerBgShape}
          alt="shape"
          width={1225}
          height={1137}
          priority
          className="absolute left-[-436.351px] top-0 w-full h-[785px] object-cover pointer-events-none select-none opacity-60 xl:opacity-100"
        />

        <Bounded as={"div"} className="relative">
             {/* Hero Blur Effects */}
            <div className="absolute bg-[#69E1CA] blur-[137px] h-[335px] w-[335px] rounded-full right-[-130px] top-[-74px] pointer-events-none" />
            <div className="absolute bg-[#69E1CA] blur-[137px] h-[349px] w-[349px] rounded-full bottom-[-187px] left-[-163px] pointer-events-none" />

          {/* Reduced gap to bring image and content closer */}
          <div className="relative z-10 flex flex-col xl:flex-row items-center xl:justify-between gap-8 xl:gap-12">
            
            {/* Left Side: Content */}
            <motion.div
              className="flex-1 order-1 xl:order-1 flex flex-col justify-center h-full"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
                <div className="space-y-6 max-w-xl">
                    {/* Header Group */}
                    <div className="space-y-4">
                        {/* Batch Label - Updated for Dark Mode */}
                        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#43D9BD]/40 bg-[#43D9BD]/10 backdrop-blur-sm">
                        <PrismicNextImage
                  className="h-6 w-6 object-cover"
                  field={slice.primary.batch_icon}
                />
                            <span className="w-2 h-2 rounded-full bg-[#43D9BD] animate-pulse"></span>
                            <span className="text-xs font-bold text-[#43D9BD] tracking-widest uppercase">
                                {slice.primary.batch_label}
                            </span>
                        </div>

                        <PrismicRichText
                            field={slice.primary.title}
                            components={{
                                label: ({ node, children }) => {
                                    if (node.data.label === "Highlight") {
                                        // Highlight color (Secondary Cyan)
                                        return <span className="text-[#43D9BD]">{children}</span>;
                                    }
                                    return <span className="text-white">{children}</span>;
                                },
                                heading1: ({ children }) => (
                                    <h1 className="text-4xl md:text-5xl text-white font-archivo font-black tracking-tight leading-[1.1]">
                                        {children}
                                    </h1>
                                ),
                                heading2: ({ children }) => (
                                    <h2 className="text-4xl md:text-5xl text-white font-archivo font-black tracking-tight leading-[1.1]">
                                        {children}
                                    </h2>
                                ),
                            }}
                        />
                    </div>

                    {/* Description - Updated to Off-White */}
                    <div className="text-[#EBF7FF]">
                        <PrismicRichText
                            field={slice.primary.description}
                            components={{
                                paragraph: ({ children }) => (
                                    <p className="font-merriweather text-[16px] leading-7 opacity-90">
                                        {children}
                                    </p>
                                ),
                            }}
                        />
                    </div>

                    {/* Features: Animated Border Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        {slice.primary.features.map((item, index) => (
                            // Wrapper with padding for border width
                            <div key={index} className="relative group h-full rounded-2xl p-[2px] overflow-hidden">
                                {/* The Rolling Line Animation - Colors adjusted for Dark Blue bg */}
                                <div className="absolute inset-[-100%] animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#033D61_0%,#43D9BD_50%,#033D61_100%)] opacity-100" />
                                
                                {/* Inner Card Content - Dark Blue background to match theme */}
                                <div className="relative h-full bg-[#033D61] rounded-[14px] p-4 z-10 flex  items-start gap-2.5 transition-all ">
                                                <PrismicNextImage
                          className="h-5 w-5 object-cover mt-1"
                          field={item.feature_icon}

                        />
                        <div>

                       
                                    <h3 className="text-white text-[17px] font-bold font-archivo mb-1 ">
                        
                                        {item.feature_title}
                                    </h3>
                                    <p className="text-[#EBF7FF]/70 text-xs font-medium leading-relaxed">
                                        {item.feature_sub_title}
                                    </p>
                                     </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* Right Side: Enhanced Image Design */}
            <motion.div
              className="relative order-2 xl:order-2 shrink-0"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
                <div className="relative w-[280px] h-[380px] md:w-[450px] md:h-[530px]">
                    
                    {/* Layer 1: The Depth Shadow - Adjusted opacity for dark bg */}
                    <div 
                        className="absolute inset-0 bg-[#69E1CA]/40 z-0 translate-x-4 translate-y-4 md:translate-x-6 md:translate-y-6"
                        style={{
                            clipPath: isLargerDevice
                            ? "path('M360.762 103.646C338.217 99.8773 213.287 78.2058 53.5993 0V133.798L140.018 177.141C140.018 177.141 111.837 173.372 74.2647 177.141C36.6921 180.91 13.2085 199.755 5.69391 221.426C-1.82066 243.098 -5.5798 279.845 21.6619 302.459C48.9036 325.072 149.412 327.899 140.018 327.899C130.625 327.899 52.657 320.361 16.9616 375.953C-18.7339 431.545 12.2621 460.755 16.962 472.062C21.6619 483.369 99.6152 521.323 173.533 536.535C251.238 549.2 334.748 537.702 404.911 523.885V410.816C337.788 418.085 249.698 417.429 188.864 410.816C188.864 410.816 335.399 384.434 363.580 371.242C391.761 358.051 404.911 337.322 404.911 299.632C404.911 261.942 389.537 239.603 341.036 228.022C382.933 215.615 404.911 188.448 404.911 157.354C404.911 126.26 383.307 107.415 360.762 103.646Z')"
                            : "path('M227.64 65.4005C213.414 63.0223 134.583 49.3476 33.821 0V84.426L88.351 111.775C88.351 111.775 70.5689 109.397 46.8608 111.775C23.1526 114.154 8.3345 126.044 3.59283 139.719C-1.14883 153.394 -3.52083 176.581 13.6686 190.85C30.858 205.12 94.2783 206.903 88.351 206.903C82.4237 206.903 33.2264 202.147 10.7027 237.225C-11.821 272.304 7.73732 290.735 10.703 297.869C13.6686 305.004 62.8568 328.953 109.499 338.552C158.53 346.543 211.225 339.288 255.497 330.57V259.224C213.143 263.81 157.559 263.396 119.172 259.224C119.172 259.224 211.636 242.576 229.418 234.253C247.2 225.929 255.497 212.849 255.497 189.067C255.497 165.285 245.796 151.189 215.193 143.881C241.629 136.053 255.497 118.91 255.497 99.2898C255.497 79.6696 241.866 67.7786 227.64 65.4005Z')",
                        }}
                    />

                    {/* Layer 2: Main Image */}
                    <div
                        className="relative overflow-hidden w-full h-full z-10"
                        style={{
                            clipPath: isLargerDevice
                            ? "path('M360.762 103.646C338.217 99.8773 213.287 78.2058 53.5993 0V133.798L140.018 177.141C140.018 177.141 111.837 173.372 74.2647 177.141C36.6921 180.91 13.2085 199.755 5.69391 221.426C-1.82066 243.098 -5.5798 279.845 21.6619 302.459C48.9036 325.072 149.412 327.899 140.018 327.899C130.625 327.899 52.657 320.361 16.9616 375.953C-18.7339 431.545 12.2621 460.755 16.962 472.062C21.6619 483.369 99.6152 521.323 173.533 536.535C251.238 549.2 334.748 537.702 404.911 523.885V410.816C337.788 418.085 249.698 417.429 188.864 410.816C188.864 410.816 335.399 384.434 363.580 371.242C391.761 358.051 404.911 337.322 404.911 299.632C404.911 261.942 389.537 239.603 341.036 228.022C382.933 215.615 404.911 188.448 404.911 157.354C404.911 126.26 383.307 107.415 360.762 103.646Z')"
                            : "path('M227.64 65.4005C213.414 63.0223 134.583 49.3476 33.821 0V84.426L88.351 111.775C88.351 111.775 70.5689 109.397 46.8608 111.775C23.1526 114.154 8.3345 126.044 3.59283 139.719C-1.14883 153.394 -3.52083 176.581 13.6686 190.85C30.858 205.12 94.2783 206.903 88.351 206.903C82.4237 206.903 33.2264 202.147 10.7027 237.225C-11.821 272.304 7.73732 290.735 10.703 297.869C13.6686 305.004 62.8568 328.953 109.499 338.552C158.53 346.543 211.225 339.288 255.497 330.57V259.224C213.143 263.81 157.559 263.396 119.172 259.224C119.172 259.224 211.636 242.576 229.418 234.253C247.2 225.929 255.497 212.849 255.497 189.067C255.497 165.285 245.796 151.189 215.193 143.881C241.629 136.053 255.497 118.91 255.497 99.2898C255.497 79.6696 241.866 67.7786 227.64 65.4005Z')",
                        }}
                    >
                        </div>
                        <div className="absolute inset-0 z-">

                        <Image src={aboutImgBg.src} alt="" fill />
                        <PrismicNextImage
                            className="object-cover object-top"
                            field={imageField}
                            fill
                            />
                            </div>
                    
                </div>

                {/* Message/Quote Box - Styled to match features using Hero Glassmorphism */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] md:w-auto md:left-auto md:right-0 md:translate-x-0 md:-bottom-[5%] z-20">
                     <div className="relative rounded-2xl p-[2px] overflow-hidden">
                        <div className="absolute inset-[-100%] animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#033D61_0%,#43D9BD_50%,#033D61_100%)]" />
                        
                        {/* Glassmorphism background matching Hero achievements cards */}
                        <div className="relative bg-[#E6F5FE45] backdrop-blur-[10px] p-5 rounded-[14px]">
                             <p className="text-white font-archivo text-base font-bold leading-tight">
                                &ldquo;{messageField}&rdquo;
                             </p>
                        </div>
                     </div>
                </div>
            </motion.div>
          </div>
        </Bounded>
      </section>
    );
  }

  // --- Original Implementation ---
  const imageField = slice.primary.left_side_image;
  const messageField = slice.primary.left_side_image_message;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={clsx(
        slice.variation === "default" ? "bg-[#EBF7FF]" : "bg-white",
        "overflow-hidden"
      )}
    >
      <Bounded as={"div"} className="xl:pt-[120px] pt-[60px] pb-40 md:pb-10">
        <div className="flex flex-col xl:flex-row items-center xl:justify-between gap-[100px]">
          {/* left side image  */}
          <motion.div
            className="relative order-2 xl:order-1"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <div
              className="relative overflow-hidden w-[255px] h-[342px] md:w-[478px] md:h-[566px]"
              style={{
                clipPath: isLargerDevice
                  ? "path('M360.762 103.646C338.217 99.8773 213.287 78.2058 53.5993 0V133.798L140.018 177.141C140.018 177.141 111.837 173.372 74.2647 177.141C36.6921 180.91 13.2085 199.755 5.69391 221.426C-1.82066 243.098 -5.5798 279.845 21.6619 302.459C48.9036 325.072 149.412 327.899 140.018 327.899C130.625 327.899 52.657 320.361 16.9616 375.953C-18.7339 431.545 12.2621 460.755 16.962 472.062C21.6619 483.369 99.6152 521.323 173.533 536.535C251.238 549.2 334.748 537.702 404.911 523.885V410.816C337.788 418.085 249.698 417.429 188.864 410.816C188.864 410.816 335.399 384.434 363.580 371.242C391.761 358.051 404.911 337.322 404.911 299.632C404.911 261.942 389.537 239.603 341.036 228.022C382.933 215.615 404.911 188.448 404.911 157.354C404.911 126.26 383.307 107.415 360.762 103.646Z')"
                  : "path('M227.64 65.4005C213.414 63.0223 134.583 49.3476 33.821 0V84.426L88.351 111.775C88.351 111.775 70.5689 109.397 46.8608 111.775C23.1526 114.154 8.3345 126.044 3.59283 139.719C-1.14883 153.394 -3.52083 176.581 13.6686 190.85C30.858 205.12 94.2783 206.903 88.351 206.903C82.4237 206.903 33.2264 202.147 10.7027 237.225C-11.821 272.304 7.73732 290.735 10.703 297.869C13.6686 305.004 62.8568 328.953 109.499 338.552C158.53 346.543 211.225 339.288 255.497 330.57V259.224C213.143 263.81 157.559 263.396 119.172 259.224C119.172 259.224 211.636 242.576 229.418 234.253C247.2 225.929 255.497 212.849 255.497 189.067C255.497 165.285 245.796 151.189 215.193 143.881C241.629 136.053 255.497 118.91 255.497 99.2898C255.497 79.6696 241.866 67.7786 227.64 65.4005Z')",
              }}
            >
            
              <Image src={aboutImgBg.src} alt="" fill />
              <PrismicNextImage
                className="object-cover object-top"
                field={slice.primary.left_side_image}
                fill
              />
            </div>
            <div className="absolute inset-0">
              <div className="relative w-full h-full">
                <div
                  className="w-full h-[292px] overflow-hidden"
                  style={{ clipPath: "inset(0 0 50%)" }}
                >
                  <PrismicNextImage
                    className="object-cover object-top "
                    field={slice.primary.left_side_image}
                    fill
                  />
                </div>
                <div className="absolute bottom-[-40%] md:bottom-0 p-6 bg-[#F5F6F6E5] rounded-[29px] text-[#033D61] font-archivo text-base font-semibold leading-6">
                  &ldquo;{slice.primary.left_side_image_message}&rdquo;
                </div>
              </div>
            </div>
          </motion.div>

          {/* right side contents  */}
          <motion.div
            className="flex-1 order-1 xl:order-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="flex items-center gap-2.5 text-lg leading-7 h-fit w-fit py-2.5 px-3.5 rounded-full bg-[#D9F0FF] font-merriweather">
                <PrismicNextImage
                  className="h-6 w-6 object-cover"
                  field={slice.primary.batch_icon}
                />
                <span className="text-primary">
                  {slice.primary.batch_label}
                </span>
              </h3>
              <div className="mt-5">
                <div className="space-y-8">
                  <PrismicRichText
                    field={slice.primary.title}
                    components={{
                      label: ({ node, children }) => {
                        if (node.data.label === "Highlight") {
                          return (
                            <span className="text-secondary">{children}</span>
                          );
                        }
                        return <span className="text-primary">{children}</span>;
                      },
                      heading1: ({ children }) => (
                        <h1 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h1>
                      ),
                      heading2: ({ children }) => (
                        <h2 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h2>
                      ),
                      heading3: ({ children }) => (
                        <h3 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h3>
                      ),
                      heading4: ({ children }) => (
                        <h4 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h4>
                      ),
                      heading5: ({ children }) => (
                        <h5 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h5>
                      ),
                      heading6: ({ children }) => (
                        <h6 className="text-4xl md:text-5xl text-primary text-archivo leading-12 md:leading-16 font-semibold">
                          {children}
                        </h6>
                      ),
                    }}
                  />
                  <PrismicRichText
                    field={slice.primary.description}
                    components={{
                      label: ({ node, children }) => {
                        if (node.data.label === "Highlight") {
                          return <span className="font-bold">{children}</span>;
                        }
                        return <span className="font-normal">{children}</span>;
                      },
                      paragraph: ({ children }) => (
                        <p className="font-merriweather text-primary tracking-wide text-[18px] leading-7 font-normal">
                          {children}
                        </p>
                      ),
                    }}
                  />
                </div>
                <div className="grid md:grid-cols-3 grid-cols-1 gap-3 mt-8 tracking-tighter ">
                  {slice.primary.features.map((item, index) => (
                    <div
                      key={index}
                      className={clsx(
                        "flex flex-col justify-center items-start rounded-xl p-4 gap-1.5",
                        slice.variation === "whiteBackground"
                          ? "bg-[#F6F9F8]"
                          : "bg-white"
                      )}
                    >
                      <div className=" p-1 rounded-lg bg-[#D9F7F2] w-fit">
                        <PrismicNextImage
                          className="h-8 w-8 object-cover"
                          field={item.feature_icon}
                        />
                      </div>
                      <h3 className="font-archivo text-[#36AE97] text-base font-semibold leading-6">
                        {item.feature_title}
                      </h3>
                      <p className="font-archivo font-medium text-sm text-[#033D61] leading-5">
                        {item.feature_sub_title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Bounded>
    </section>
  );
};

export default AboutSection;
